/**
 * 绘本创建状态管理 - Zustand Store
 * 管理三步创建流程的状态
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type CreationStep = 1 | 2 | 3;

export interface SupportingCharacterData {
  id: string;
  name: string;
  species: string;
  appearance: string;
  relationship?: string;
  characterType?: 'human' | 'animal';
}

export interface CharacterData {
  photo?: File;
  photoPreview?: string;
  photoUrl?: string;
  identityCardUrl?: string;
  name: string;
  species?: string;
  customSpecies?: string;
  traits: string[];
  appearance: string;
  characterType?: 'human' | 'animal';
  gender?: 'boy' | 'girl';
}

export interface StoryData {
  age: string;
  pages: number;
  theme: string;
  tags: string[];   // 主题标签（支持多标签）
  outline: string;  // 故事梗概（可选）
  speed?: number;   // 语速 (0.7 ~ 1.3)，根据年龄自动设置默认值
}

export interface VisualData {
  style: string;
  model: 'glm-5v-turbo';
  voice: string;
}

export interface CreationState {
  // 当前步骤
  step: CreationStep;
  
  // 创作模式：ai 或 diy
  flow: 'ai' | 'diy';
  
  // 各步骤数据
  character: CharacterData;
  supportingCharacters: SupportingCharacterData[];
  story: StoryData;
  visual: VisualData;
  
  // 动作
  setStep: (step: CreationStep) => void;
  setFlow: (flow: 'ai' | 'diy') => void;
  setCharacter: (data: Partial<CharacterData>) => void;
  addSupportingCharacter: (char: SupportingCharacterData) => void;
  removeSupportingCharacter: (id: string) => void;
  updateSupportingCharacter: (id: string, data: Partial<SupportingCharacterData>) => void;
  setStory: (data: Partial<StoryData>) => void;
  setVisual: (data: Partial<VisualData>) => void;
  
  // 验证
  isStep1Valid: () => boolean;
  isStep2Valid: () => boolean;
  isStep3Valid: () => boolean;
  
  // 重置
  reset: () => void;
  
  // 获取完整数据
  getCreationData: () => {
    character: CharacterData;
    supportingCharacters: SupportingCharacterData[];
    story: StoryData;
    visual: VisualData;
  };
}

const initialState = {
  step: 1 as CreationStep,
  flow: 'ai' as 'ai' | 'diy',
  character: {
    name: '',
    species: '',
    customSpecies: '',
    traits: [] as string[],
    appearance: '',
    characterType: undefined as 'human' | 'animal' | undefined,
    gender: undefined as 'boy' | 'girl' | undefined,
    photo: undefined as File | undefined,
    photoPreview: '',
    photoUrl: '',
    identityCardUrl: '',
  },
  supportingCharacters: [] as SupportingCharacterData[],
  story: {
    age: '5-6',
    pages: 6,
    theme: '',
    tags: [],
    outline: '',  // 故事梗概初始为空
    speed: 0.9,
  },
  visual: {
    style: '',  // 不预设默认风格，由用户自行选择
    model: 'glm-5v-turbo' as 'glm-5v-turbo',
    voice: 'xiaoyan',
  },
};

export const useCreationStore = create<CreationState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setStep: (step) => set({ step }),

      setFlow: (flow) => set({ flow }),

      setCharacter: (data) =>
        set((state) => ({
          character: { ...state.character, ...data },
        })),

      addSupportingCharacter: (char) =>
        set((state) => ({
          supportingCharacters: [...state.supportingCharacters, char],
        })),

      removeSupportingCharacter: (id) =>
        set((state) => ({
          supportingCharacters: state.supportingCharacters.filter((c) => c.id !== id),
        })),

      updateSupportingCharacter: (id, data) =>
        set((state) => ({
          supportingCharacters: state.supportingCharacters.map((c) =>
            c.id === id ? { ...c, ...data } : c
          ),
        })),

      setStory: (data) =>
        set((state) => ({
          story: { ...state.story, ...data },
        })),

      setVisual: (data) =>
        set((state) => ({
          visual: { ...state.visual, ...data },
        })),

      isStep1Valid: () => {
        const { character, flow } = get();
        const hasPhoto = !!(character.photoUrl || character.photoPreview);
        // DIY 模式下照片可选，但外观描述必填；AI 模式下必须有照片
        const photoValid = flow === 'diy' ? true : hasPhoto;
        // 外观描述必填（DIY 模式下靠文字描述，AI 模式下需要确认 AI 提取结果）
        const appearanceValid = character.appearance.trim().length > 0;
        // 角色类型必须选择
        const typeValid = !!character.characterType;
        // 人物/动物类型都必须选择性别（动物用公/母，人物用男/女）
        const genderValid = !!character.gender;
        return !!(
          character.name.trim() &&
          character.traits.length > 0 &&
          photoValid &&
          appearanceValid &&
          typeValid &&
          genderValid
        );
      },

      isStep2Valid: () => {
        const { story, flow } = get();
        // 基础字段始终必填
        const baseValid = !!(story.age && story.pages);
        // AI 模式下主题和梗概必填；DIY 模式下可选
        const aiFieldsValid = flow === 'diy' ? true : !!(story.theme && story.outline.trim());
        return baseValid && aiFieldsValid;
      },

      isStep3Valid: () => {
        const { visual } = get();
        return !!visual.style;
      },

      reset: () => set(initialState),

      getCreationData: () => ({
        character: get().character,
        supportingCharacters: get().supportingCharacters,
        story: get().story,
        visual: get().visual,
      }),
    }),
    {
      name: 'creation-store',
      // 禁用自动 rehydrate，避免与组件内 setState 竞争（在 App.tsx 中手动 rehydrate）
      skipHydration: true,
      // 不持久化 photoPreview（base64图片太大，超过localStorage 5MB限制）
      partialize: (state) => ({
        step: state.step,
        character: {
          name: state.character.name,
          species: state.character.species,
          customSpecies: state.character.customSpecies,
          traits: state.character.traits,
          appearance: state.character.appearance,
          characterType: state.character.characterType,
          gender: state.character.gender,
          photoUrl: state.character.photoUrl,
          identityCardUrl: state.character.identityCardUrl,
          // photoPreview 不持久化，避免QuotaExceededError；photoUrl 是 COS 地址，可以持久化
        },
        supportingCharacters: state.supportingCharacters,
        flow: state.flow,
        story: {
          age: state.story.age,
          pages: state.story.pages,
          theme: state.story.theme,
          tags: state.story.tags,
          outline: state.story.outline,
          speed: state.story.speed,
        },
        visual: state.visual,
      }),
    }
  )
);
